import {
  Grid,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import MainCard from "components/MainCard";
import SimpleBar from "components/third-party/SimpleBar";
import { IProgramAndYearWiseMember } from "../../api/types";
import LoadingButton from "@mui/lab/LoadingButton";
import { AiFillFileExcel } from "react-icons/ai";

type Props = {
  data?: IProgramAndYearWiseMember[];
  exportProgram?: () => void;
  loading?: boolean;
};

export default function ProgramWiseCount({
  data,
  exportProgram,
  loading,
}: Props) {
  if (!data || data.length === 0) {
    return (
      <MainCard darkTitle title="Program Wise Passout Members">
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "200px" }}
        >
          <Typography variant="h6" color="textSecondary">
            No data found.
          </Typography>
        </Grid>
      </MainCard>
    );
  }
  const { years, rows, yearlyTotals, grandTotal } = pivotProgramData(data);

  return (
    <MainCard
      darkTitle
      title="Program Wise Passout Members"
      content={false}
      secondary={
        <LoadingButton
          size="small"
          variant="contained"
          loading={loading}
          loadingPosition="start"
          color="secondary"
          startIcon={<AiFillFileExcel size={18} />}
          onClick={exportProgram}
        >
          Export
        </LoadingButton>
      }
    >
      <TableContainer sx={{ maxHeight: 490 }}>
        <SimpleBar sx={{ width: "100%", height: 440, overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead
              sx={{
                position: "sticky",
                top: -1,
                zIndex: 2,
              }}
            >
              <TableRow>
                <TableCell>Program</TableCell>
                {years.map((year) => (
                  <TableCell key={year} align="center">
                    {year}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: -1,
                    zIndex: 5,
                  }}
                >
                  <strong>Total</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.programName}</TableCell>
                  {row.counts.map((count, i) => (
                    <TableCell key={i} align="center">
                      {count > 0 ? count : "-"}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <strong>{row.total}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "white",
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                {years.map((_, i) => (
                  <TableCell key={i} align="center">
                    <strong>
                      {yearlyTotals[i] > 0 ? yearlyTotals[i] : "-"}
                    </strong>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <strong>{grandTotal}</strong>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </SimpleBar>
      </TableContainer>
    </MainCard>
  );
}

function pivotProgramData(data: IProgramAndYearWiseMember[]) {
  const yearSet = new Set<number>();
  const programMap = new Map<string, { [year: number]: number }>();

  data.forEach(({ programName, year, memberCount }) => {
    yearSet.add(year);
    if (!programMap.has(programName)) {
      programMap.set(programName, {});
    }
    const yearMap = programMap.get(programName)!;
    yearMap[year] = (yearMap[year] || 0) + memberCount;
  });

  const years = Array.from(yearSet).sort((a, b) => a - b);

  const rows = Array.from(programMap.entries()).map(
    ([programName, yearMap]) => {
      const counts = years.map((year) => yearMap[year] || 0);
      const total = counts.reduce((sum, val) => sum + val, 0);
      return { programName, counts, total };
    }
  );

  const yearlyTotals = years.map((_, idx) =>
    rows.reduce((sum, row) => sum + row.counts[idx], 0)
  );

  const grandTotal = yearlyTotals.reduce((sum, val) => sum + val, 0);

  return { years, rows, yearlyTotals, grandTotal };
}
